<script>
import Common from '../../common/Common.vue'
import Endpoints from '../../common/Endpoints.vue'
import Services from '../../common/Services.vue'
import moment from 'moment-timezone'
import { createDefaultMonitorEventFilters, getEnabledMonitorEventTypes, normalizeMonitorEventFilters } from '../../utils/events'
import { applySessionScope } from "../../utils/scope"

export default {
    init: async payload => {
        try {
            payload.domain = Endpoints.domain

            payload.user = await payload.$session.get('user')
            payload.isSuperAdminMaster = await Common.isSuperAdminMaster(payload)
            payload.role = await Common.getSubtype(payload)
            payload.attendancesTypes = await Services.getAttendancesOptionsTypes(payload)

            if (payload.isSuperAdminMaster) {
                payload.accounts = await Services.getAccounts(payload)
            } else if (payload.role === 'ADMIN') {
                payload.clients = await Services.getClients(payload)
                payload.filters.account = await Common.getAccountId(payload)
            } else if (payload.role === 'MANAGER') {
                payload.clients = await Services.getClients(payload)
                payload.filters.account = await Common.getAccountId(payload)
                payload.filters.client = await Common.getClientId(payload)
            } else if (payload.role === 'OPERATOR') {
                payload.sites = await Services.getSites(payload)
                payload.filters.account = await Common.getAccountId(payload)
                payload.filters.client = await Common.getClientId(payload)
                payload.filters.sites = await Common.getSiteGroupId(payload)
            }

            await payload.loadMonitorEventSettings({ shouldFilter: true })
        } catch (_error) {
            /* handle initialization error */
        }
    },
    methods: {
        getEnabledMonitorEventTypesSet() {
            const enabledTypes = Array.isArray(this.filters?.types)
                ? this.filters.types.filter(type => typeof type === 'string' && type.trim().length > 0)
                : []
            return new Set(enabledTypes)
        },
        isMonitorEventTypeEnabled(type) {
            const normalizedType = typeof type === 'string' ? type.trim() : ''
            if (!normalizedType) return false

            return this.getEnabledMonitorEventTypesSet().has(normalizedType)
        },
        resolveMonitorEventType(item) {
            const rawType = typeof item?.type === 'string' ? item.type.trim() : ''
            const source = typeof item?.source === 'string' ? item.source.trim().toUpperCase() : ''

            if (rawType === 'SCAN' && item?.geofence?.status === 'OUTSIDE' && source !== 'ALERTPORT') {
                return 'OUTSIDE_GEOFENCE'
            }

            return rawType
        },
        filterPatrolActionsByEnabledTypes(items) {
            const source = Array.isArray(items) ? items : []
            const enabledTypesSet = this.getEnabledMonitorEventTypesSet()

            if (enabledTypesSet.size === 0) {
                return []
            }

            return source.filter(item => {
                const type = this.resolveMonitorEventType(item)
                return type ? enabledTypesSet.has(type) : false
            })
        },
        async loadMonitorEventSettings({ shouldFilter = false } = {}) {
            const accountId = this.filters.account || this.user?.account?._id || ''

            if (!accountId) {
                this.companySettings = {
                    account: '',
                    status: 'ACTIVE',
                    monitor: {
                        eventFilters: createDefaultMonitorEventFilters(),
                    },
                }
                this.monitorEventFilters = createDefaultMonitorEventFilters()
                this.filters.types = []
                this.items = []
                this.events = []
                this.selectedEvent = null
                this.attendances = []
                return
            }

            this.isLoadingCompanySettings = true

            try {
                const settings = await Services.getCompanySettings(this, { account: accountId })
                this.companySettings = settings
                this.monitorEventFilters = normalizeMonitorEventFilters(settings?.monitor?.eventFilters)
                this.filters.types = getEnabledMonitorEventTypes(this.monitorEventFilters)
                if (typeof this.applyCallRecordingPolicy === 'function') {
                    this.applyCallRecordingPolicy(settings?.monitor?.callRecordingEnabled)
                }
            } catch (_error) {
                this.companySettings = {
                    account: accountId,
                    status: 'ACTIVE',
                    monitor: {
                        eventFilters: createDefaultMonitorEventFilters(),
                    },
                }
                this.monitorEventFilters = createDefaultMonitorEventFilters()
                this.filters.types = getEnabledMonitorEventTypes(this.monitorEventFilters)
                if (typeof this.applyCallRecordingPolicy === 'function') {
                    this.applyCallRecordingPolicy(false)
                }
            } finally {
                this.isLoadingCompanySettings = false
            }

            if (shouldFilter) {
                await this.filter()
            }
        },
        getOperatorId: async function () {
            return this.$session.get('user')?._id
        },
        getMonitorTimezone() {
            return Common.resolveTimezoneId(this.user?.accountTimezone || this.user?.account?.timezone)
        },
        parseEventDateInAccountTimezone(date) {
            const timezone = this.getMonitorTimezone()
            if (!date) return moment.invalid()

            // @ts-ignore
            const hasTimezoneInfo = typeof date === 'string' && /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(date.trim())
            const parsedDate = hasTimezoneInfo ? moment(date) : moment.utc(date)
            if (!parsedDate.isValid()) return moment.invalid()

            return parsedDate.tz(timezone)
        },
        async filter() {
            if (!applySessionScope(this, this.filters)) return

            if (this.isSuperAdminMaster && !this.filters.account) {
                return
            }

            if (this.filters?.types?.length === 0) {
                this.items = []
                this.events = []
                this.selectedEvent = null
                this.attendances = []
                return
            }

            if (this.selectedEvent && !this.filters.types.includes(this.selectedEvent.type)) {
                this.selectedEvent = null
            }

            // Reset pagination state and load first page
            this.monitorPage = 1
            this.allEventsLoaded = false
            this.items = []
            this.events = []
            this.isLoadingEvents = true

            const selectedEventId = this.selectedEvent?._id || ''

            try {
                await this._loadEventsPage(1, true)

                if (selectedEventId) {
                    const selectedEvent = this.events.find(event => event?._id === selectedEventId)
                    if (selectedEvent) {
                        this.selectedEvent = JSON.parse(JSON.stringify(selectedEvent))
                    } else {
                        this.selectedEvent = null
                        this.attendances = []
                    }
                }
            } catch (_error) {
                /* handle filter error */
            } finally {
                this.isLoadingEvents = false
            }
        },
        async _loadEventsPage(page, isFirstPage = false) {
            const PAGE_SIZE = 10
            const filtersWithPagination = {
                ...this.filters,
                // Tela de vigilância exibe somente eventos originados do shieldgo-app.
                // Eventos carimbados com source='ALERTPORT' são roteados para /monitor/alert-occurrence.
                excludeSources: ['ALERTPORT'],
                limit: PAGE_SIZE,
                skip: page,
            }

            const { results, totalCount } = await Services.getPatrolActionsPaginated(this, filtersWithPagination)
            const filteredResults = this.filterPatrolActionsByEnabledTypes(results)
            const formattedEvents = await this.formatPatrolActions(filteredResults)

            if (isFirstPage) {
                this.items = filteredResults
                this.events = formattedEvents
            } else {
                this.items = [...this.items, ...filteredResults]
                this.events = [...this.events, ...formattedEvents]
            }

            this.totalEventsCount = totalCount != null ? totalCount : (this.events.length)
            this.monitorPage = page

            if (results.length < PAGE_SIZE) {
                this.allEventsLoaded = true
            }
        },
        async loadMoreEvents() {
            if (this.isLoadingMoreEvents || this.allEventsLoaded) return
            if (this.isSuperAdminMaster && !this.filters.account) return

            this.isLoadingMoreEvents = true
            try {
                await this._loadEventsPage(this.monitorPage + 1, false)
            } catch (_error) {
                /* handle load more events error */
            } finally {
                this.isLoadingMoreEvents = false
            }
        },
        checkRequiredField(field) {
            return this.errors.includes(field)
        },
        removeRequiredField(field) {
            this.errors = this.errors.filter(item => item !== field)
        },
        handleAttendanceEvent: async function () {
            if (this.isLoadingAttendanceEventButton || this.attendanceEventEnabled) return

            this.attendanceEventEnabled = true
            this.isLoadingAttendanceEventButton = true

            const patrolActionId = this.selectedEvent?._id
            const userId = await this.getOperatorId()
            const siteGroup = this.$session.get('user')?.siteGroup?._id

            const attendance = {
                isAttendance: true,
                openedDate: moment().utc().format(),
                operator: userId,
                status: 'IN_PROGRESS',
            }

            try {
                const result = await Services.attendanceEvent(this, patrolActionId, attendance, siteGroup)
                if (result && this.selectedEvent) {
                    this.selectedEvent.attendance = {
                        isAttendance: result?.attendance?.isAttendance,
                        openedDate: result?.attendance?.openedDate,
                        closedDate: result?.attendance?.closedDate,
                        operator: this.user,
                        status: result?.attendance?.status,
                    }
                }

                this.attendanceEventEnabled = false
                this.isLoadingAttendanceEventButton = false
                this.clearForm()
            } catch (_error) {
                this.attendanceEventEnabled = false
                this.isLoadingAttendanceEventButton = false
                this.clearForm()
            }
        },
        closeAttendance: async function () {
            this.attendanceEventEnabled = true
            this.isLoadingAttendanceEventButton = true

            const patrolActionId = this.selectedEvent?._id
            const openedDate = this.selectedEvent.attendance?.openedDate
            const userId = await this.getOperatorId()
            const siteGroup = this.$session.get('user')?.siteGroup?._id

            const attendance = {
                isAttendance: true,
                openedDate: openedDate,
                closedDate: moment().utc().format(),
                operator: userId,
                status: 'CLOSED',
            }

            try {
                const result = await Services.attendanceEvent(this, patrolActionId, attendance, siteGroup)
                if (result && this.selectedEvent) {
                    this.selectedEvent.attendance = {
                        isAttendance: result?.attendance?.isAttendance,
                        openedDate: openedDate,
                        closedDate: result?.attendance?.closedDate,
                        operator: this.user,
                        status: result?.attendance?.status,
                    }
                }

                this.attendanceEventEnabled = false
                this.isLoadingAttendanceEventButton = false
                this.clearForm()
            } catch (_error) {
                this.attendanceEventEnabled = false
                this.isLoadingAttendanceEventButton = false
                this.clearForm()
            }
        },
        confirmCloseAttendance: async function () {
            if (!this.attendances || this.attendances.length === 0) {
                Common.show(this, 'bottom-right', 'error', this.$t('str.monitor.register.message.require'))
                return
            }

            this.$swal({
                title: this.$t('str.are.you.sure'),
                text: this.$t('str.are.you.sure.close'),
                showCancelButton: true,
                buttonsStyling: false,
                confirmButtonText: this.$t('str.title.close.attendance'),
                cancelButtonText: this.$t('str.title.cancel'),
                confirmButtonClass: 'btn me-5px btn-danger',
                cancelButtonClass: 'btn btn-default',
            }).then(result => {
                if (result.isConfirmed && result.value) {
                    this.closeAttendance()
                }
            })
        },
        sendAttendanceEvent: async function () {
            if (!this.isLoadingSendAttendanceButton) {
                this.isLoadingSendAttendanceButton = true

                const attendance = {
                    account: this.selectedEvent?.account?._id,
                    client: this.selectedEvent?.client?._id,
                    site: this.selectedEvent?.site?._id,
                    event: this.selectedEvent?.event?._id,
                    type: this.attendance?.type,
                    notes: this.attendance?.notes,
                    patrolAction: this.selectedEvent?._id,
                    createDate: moment().utc().format(),
                    status: 'ACTIVE',
                }

                attendance.siteGroup = await this.$session.get('user')?.siteGroup?._id
                attendance.operator = await this.getOperatorId()

                try {
                    await Services.createAttendance(this, attendance)

                    const filters = {
                        patrolAction: this.selectedEvent?._id,
                        status: 'ACTIVE',
                    }

                    this.attendances = await Services.getEventAttendances(this, filters)
                    this.clearForm()

                    this.attendanceEventEnabled = false
                    this.isLoadingSendAttendanceButton = false
                    this.isLoadingAttendanceList = true
                    this.isLoadingAttendanceList = false
                } catch (_error) {
                    this.isLoadingSendAttendanceButton = false
                    this.isLoadingAttendanceList = false
                    Common.show(this, 'bottom-right', 'danger', 'str.error_creating_attendance')
                }
            }
        },
        clearForm: function () {
            this.attendance.type = ''
            this.attendance.notes = ''
        },
        formatPatrolActions: async function (list) {
            const data = list.map(item => {
                const {
                    _id,
                    status,
                    account,
                    client,
                    site,
                    event,
                    type: rawType,
                    date,
                    vigilant,
                    patrol,
                    patrolPoint,
                    soundURL,
                    photoURL,
                    signatureURL,
                    failurePatrolType,
                    incidents,
                    deviceInfo,
                    geolocation,
                    geofence,
                    notes,
                    attendance,
                    source,
                } = item
                const type = this.resolveMonitorEventType(item)

                return {
                    _id: _id,
                    vigilant,
                    patrol,
                    soundURL,
                    photoURL,
                    signatureURL,
                    event,
                    title: type,
                    type: type,
                    rawType,
                    account,
                    client,
                    site,
                    source,
                    failurePatrolType,
                    incidents: incidents || [],
                    deviceInfo: deviceInfo || {},
                    geolocation: geolocation || {},
                    geofence: geofence || {},
                    patrolPoint,
                    notes: notes ? notes : '',
                    date,
                    attendance,
                    status: status,
                }
            })

            const formatData = []
            data.forEach(item => {
                const dataItem = item

                let vigilant = dataItem?.vigilant?.firstName ? dataItem?.vigilant?.firstName : ''
                vigilant += dataItem?.vigilant?.lastName ? ' ' + dataItem?.vigilant?.lastName + ' ' : ''

                dataItem.description = vigilant

                switch (dataItem?.type) {
                    case 'START_PATROL':
                        {
                            dataItem.description += this.$t('str.event.started_a_patrol')
                            dataItem.tag = 'badge bg-gray-300 text-gray-900 me-1'
                            dataItem.icon = 'fa fa-play-circle'
                        }
                        break
                    case 'END_PATROL':
                        {
                            dataItem.description += this.$t('str.event.ended_a_patrol')
                            dataItem.tag = 'badge bg-gray-300 text-gray-900 me-1'
                            dataItem.icon = 'fa fa-stop-circle'
                        }
                        break
                    case 'INCIDENT':
                        {
                            dataItem.description += this.$t('str.event.reported_an_incident')
                            dataItem.tag = 'badge bg-orange text-white me-1'
                            dataItem.icon = 'fa fa-exclamation-circle'
                        }
                        break
                    case 'SOS_ALERT':
                        {
                            dataItem.description += this.$t('str.event.reported_an_alert')
                            dataItem.tag = 'badge bg-danger text-white me-1'
                            dataItem.icon = 'fa fa-bell'
                        }
                        break
                    case 'SCAN':
                        {
                            dataItem.description += this.$t('str.event.scanned_a_qr_code')
                            dataItem.tag = 'badge bg-indigo text-white me-1'
                            dataItem.icon = 'fa fa-qrcode'
                        }
                        break

                    case 'CANCEL_PATROL':
                        {
                            dataItem.description += this.$t('str.event.cancel_patrol')
                            dataItem.tag = 'badge bg-indigo text-white me-1'
                            dataItem.icon = 'fa fa-ban'
                        }
                        break

                    case 'FAILURE_PATROL':
                        {
                            dataItem.description += this.$t('str.event.failure_patrol')

                            if (item?.event?.name) {
                                dataItem.description += ' ' + item?.event?.name + ' '
                            } else {
                                dataItem.description += ' '
                            }

                            dataItem.tag = 'badge dark-orange me-1'
                            dataItem.icon = 'fa fa-exclamation-triangle'
                        }
                        break

                    case 'OUTSIDE_GEOFENCE':
                        {
                            const details = this.getOutsideGeofenceDetails(dataItem)
                            dataItem.description += `${this.$t('str.event.outside_geofence')} ${details.point}. `
                            dataItem.description += `${this.$t('str.monitor.details.geofence.distance')}: ${details.distance}; `
                            dataItem.description += `${this.$t('str.monitor.details.geofence.radius')}: ${details.radius}; `
                            dataItem.description += `${this.$t('str.monitor.details.geofence.accuracy')}: ${details.accuracy}.`
                            dataItem.tag = 'badge bg-danger text-white me-1'
                            dataItem.icon = 'fa fa-map-marker-alt'
                        }
                        break

                    default:
                        break
                }

                if (dataItem?.failurePatrolType) {
                    const dateTimezone = this.parseEventDateInAccountTimezone(dataItem?.event?.startDate)
                    const formattedDate = dateTimezone.isValid() ? dateTimezone.format('DD/MM/YYYY') : ''
                    const formattedTime = dateTimezone.isValid() ? dateTimezone.format('HH:mm') : ''

                    switch (dataItem.failurePatrolType) {
                        case 'NOT_STARTED':
                            {
                                dataItem.failureText = this.$t('str.msg.not.started.patrol')
                                dataItem.description += this.$t('str.event.failure_patrol.scheduled.at.description') + ' '

                                dataItem.description += formattedDate + ' - ' + formattedTime + ' ' + this.$t('str.event.failure_patrol.description')
                            }
                            break
                        case 'INCOMPLETE':
                            {
                                dataItem.failureText = this.$t('str.msg.incomplete.patrol')
                                dataItem.description += this.$t('str.event.failure_patrol.scheduled.at.description') + ' '
                                dataItem.description += formattedDate + ' - ' + formattedTime + ' ' + this.$t('str.event.incomplete_patrol.description')
                            }

                            break
                        case 'EXPIRED':
                            {
                                dataItem.failureText = this.$t('str.msg.expired.patrol')
                                dataItem.description += this.$t('str.event.failure_patrol.scheduled.at.description') + ' '
                                dataItem.description += formattedDate + ' - ' + formattedTime + ' ' + this.$t('str.event.expired_patrol.description')
                            }
                            break
                        default:
                            break
                    }
                } else {
                    dataItem.failureText = this.$t(dataItem.type)
                }

                const event = dataItem?.event ? dataItem?.event : null
                if (event) {
                    switch (event?.status) {
                        case 'ACTIVE':
                            event.tag = 'badge bg-info text-white me-1'
                            break
                        case 'ÍN_PROGRESS':
                            event.tag = 'badge bg-success text-white me-1'
                            break
                        case 'FINISHED':
                            event.tag = 'badge bg-success text-white me-1'
                            break
                        case 'CANCELED':
                            event.tag = 'badge bg-danger text-white me-1'
                            break
                        default:
                            event.tag = 'badge bg-gray-300 text-gray-900 me-1'
                            break
                    }
                    dataItem.event = event
                }

                let dateToUse = dataItem.date
                if (dataItem.failurePatrolType === 'NOT_STARTED' && dataItem?.event?.startDate) {
                    dateToUse = dataItem.event.startDate
                }

                const dateTimezone = this.parseEventDateInAccountTimezone(dateToUse)
                const formattedDate = dateTimezone.isValid() ? dateTimezone.format('DD/MM/YYYY') : ''
                const formattedTime = dateTimezone.isValid() ? dateTimezone.format('HH:mm') : ''

                dataItem.formattedDate = formattedDate
                dataItem.formattedTime = formattedTime

                dataItem.description += ' ' + this.$t('str.event.on') + ' ' + formattedDate + ' - ' + formattedTime

                formatData.push(dataItem)
            })

            return formatData
        },
        formatDate: function (date) {
            const dateTimezone = this.parseEventDateInAccountTimezone(date)
            if (!dateTimezone.isValid()) return ''
            const formattedDate = dateTimezone.format('DD/MM/YYYY')
            const formattedTime = dateTimezone.format('HH:mm')

            return formattedDate + ' - ' + formattedTime
        },
        handleSelectEvent: async function (event) {
            this.selectedEvent = JSON.parse(JSON.stringify(event))
            this.attendanceEventEnabled = false
            this.removeRequiredField('attendanceOptions')

            //get attendances for event
            const filters = {
                patrolAction: event?._id,
                status: 'ACTIVE',
            }

            this.attendances = await Services.getEventAttendances(this, filters)
        },
        entityId(value) {
            if (value === null || value === undefined) return ''
            if (typeof value === 'object') return String(value._id || value.id || '')
            return String(value)
        },
        vigilantName(vigilant) {
            if (!vigilant) return '-'
            if (typeof vigilant !== 'object') return String(vigilant)
            return vigilant.fullName || [vigilant.firstName, vigilant.lastName].filter(Boolean).join(' ') || vigilant.name || '-'
        },
        entityName(entity) {
            if (!entity) return '-'
            if (typeof entity !== 'object') return String(entity)
            return entity.name || entity.description || entity.patrolPointCode || this.entityId(entity) || '-'
        },
        formatGeofenceMeters(value) {
            const parsed = Number(value)
            if (!Number.isFinite(parsed)) return '-'
            return `${Math.round(parsed * 10) / 10} m`
        },
        getOutsideGeofenceDetails(event) {
            const geofence = event?.geofence || {}
            return {
                point: this.entityName(event?.patrolPoint),
                vigilant: this.vigilantName(event?.vigilant),
                distance: this.formatGeofenceMeters(geofence.distanceMeters),
                radius: this.formatGeofenceMeters(geofence.allowedRadiusMeters),
                accuracy: this.formatGeofenceMeters(geofence.scanAccuracyMeters ?? event?.geolocation?.accuracy),
                time: this.formatDate(geofence.evaluatedAt || event?.date),
            }
        },
        async enrichOutsideGeofenceEvent(event) {
            const enriched = { ...event }
            const siteId = this.entityId(enriched.site)
            const vigilantId = this.entityId(enriched.vigilant)
            const patrolPointId = this.entityId(enriched.patrolPoint)

            if (siteId && typeof enriched.site !== 'object') {
                enriched.site = (this.sites || []).find(site => this.entityId(site) === siteId) || enriched.site
            }

            if (!siteId || (!vigilantId && !patrolPointId)) return enriched

            try {
                const [vigilants, patrolPoints] = await Promise.all([
                    vigilantId && typeof enriched.vigilant !== 'object' ? Services.getVigilantsBySite(this, siteId) : Promise.resolve([]),
                    patrolPointId && typeof enriched.patrolPoint !== 'object' ? Services.getPatrolPointsBySite(this, siteId) : Promise.resolve([]),
                ])

                if (vigilantId && typeof enriched.vigilant !== 'object') {
                    enriched.vigilant = (vigilants || []).find(vigilant => this.entityId(vigilant) === vigilantId) || enriched.vigilant
                }
                if (patrolPointId && typeof enriched.patrolPoint !== 'object') {
                    enriched.patrolPoint = (patrolPoints || []).find(point => this.entityId(point) === patrolPointId) || enriched.patrolPoint
                }
            } catch (_error) {
                // The event still remains visible with ids when lookup enrichment fails.
            }

            return enriched
        },
        showOutsideGeofenceNotification(event) {
            const details = this.getOutsideGeofenceDetails(event)
            const text = [
                `${this.$t('str.monitor.details.geofence.point')}: ${details.point}`,
                `${this.$t('str.monitor.details.geofence.vigilant')}: ${details.vigilant}`,
                `${this.$t('str.monitor.details.geofence.distance')}: ${details.distance}`,
                `${this.$t('str.monitor.details.geofence.radius')}: ${details.radius}`,
                `${this.$t('str.monitor.details.geofence.accuracy')}: ${details.accuracy}`,
                `${this.$t('str.monitor.details.geofence.time')}: ${details.time}`,
            ].join(' · ')

            this.$notify({
                group: 'top-right',
                type: 'warn',
                title: this.$t('msg.outside.geofence.notification'),
                text,
            })
        },
        async handleOutsideGeofenceNotification(payload = {}) {
            if (String(payload?.source || '').trim().toUpperCase() === 'ALERTPORT') return false
            if (!this.isMonitorEventTypeEnabled('OUTSIDE_GEOFENCE')) return false

            const patrolActionId = payload?.patrolActionId
            if (!patrolActionId) return false

            const action = await Services.getPatrolActionById(this, patrolActionId)
            if (!action?._id) return false

            const mergedAction = await this.enrichOutsideGeofenceEvent({
                ...action,
                source: action.source || payload.source || 'SHIELDGO',
                geofence: {
                    ...(action.geofence || {}),
                    ...(payload.geofence || {}),
                    status: payload?.geofence?.status || action?.geofence?.status || 'OUTSIDE',
                },
            })

            if (this.resolveMonitorEventType(mergedAction) !== 'OUTSIDE_GEOFENCE') return false

            const [formattedEvent] = await this.formatPatrolActions([mergedAction])
            if (!formattedEvent) return false

            this.items = [mergedAction, ...(this.items || []).filter(item => item?._id !== formattedEvent._id)]
            this.events = [formattedEvent, ...(this.events || []).filter(item => item?._id !== formattedEvent._id)]

            try {
                await this.handleSelectEvent(formattedEvent)
            } catch (_error) {
                this.selectedEvent = JSON.parse(JSON.stringify(formattedEvent))
                this.attendances = []
            }

            this.showOutsideGeofenceNotification(formattedEvent)
            return true
        },
        changeAccount: async function () {
            const account = this.filters.account

            this.filters.client = ''
            this.filters.site = ''
            this.selectedEvent = null
            this.clients = await Services.getClientsByAccount(this, account)
            this.sites = []

            await this.loadMonitorEventSettings({ shouldFilter: true })
        },
        changeClient: async function () {
            const client = this.filters.client

            this.filters.site = ''
            this.selectedEvent = null
            this.sites = await Services.getSitesByClient(this, client)

            await this.filter()
        },
        checkForm: async function () {
            if (!this.attendance?.type || this.attendance?.type === '') {
                this.errors.push('attendanceOptions')
            }

            if (!this.errors || this.errors.length === 0) {
                await this.sendAttendanceEvent()
            }
        },
        updateSelectedEvent: async function (patrolAction) {
            const rawEvent = await Services.getPatrolActionById(this, patrolAction)
            const eventType = this.resolveMonitorEventType(rawEvent)
            const event = eventType === 'OUTSIDE_GEOFENCE' ? await this.enrichOutsideGeofenceEvent(rawEvent) : rawEvent

            if (!this.isMonitorEventTypeEnabled(eventType)) {
                this.selectedEvent = null
                return
            }

            this.selectedEvent = event
        },
        showMap() {
            this.$bvModal.show('mapModal')
        },
        showPhoto() {
            this.$bvModal.show('photoModal')
        },
        showSignature() {
            this.$bvModal.show('signatureModal')
        },
        showSound() {
            this.$bvModal.show('soundModal')
        },
        showDeviceInfo() {
            this.$bvModal.show('deviceInfoModal')
        },
        show() {
            Common.show(this, 'bottom-right', 'error', this.$t('response.user.invalid.subtype'))
        },
    },
}
</script>
